import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Ruler, TrendingDown, Activity, TrendingUp, Loader2, Plus, History, Wand2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const PERIOD_LABEL = { day: "dia", week: "semana", use: "uso" };
const UNIT_OPTIONS = ["L", "ml", "kg", "g", "un", "rolo"];

/**
 * Duration estimator — opens from the ruler button on a product row.
 *
 * The user defines:
 *  - pack_size + unit_label (e.g., 5 L per bottle)
 *  - 3 consumption levels (leve / médio / alto) per period (day | week | use)
 *
 * The system calculates how long the current monthly stock will last for
 * each scenario, in human-friendly units (days/semanas/usos and rounded
 * to 1 decimal). Updates persist via PATCH /admin/ledger/{id}.
 */
export default function DurationEstimatorModal({ open, row, onClose, onSaved }) {
  const [packSize, setPackSize] = useState(row?.pack_size ?? 1);
  const [unitLabel, setUnitLabel] = useState(row?.unit_label ?? "L");
  const [period, setPeriod] = useState(row?.consumption_period ?? "day");
  const [light, setLight] = useState(row?.consumption_light ?? 0.5);
  const [medium, setMedium] = useState(row?.consumption_medium ?? 1);
  const [high, setHigh] = useState(row?.consumption_high ?? 2);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState({ items: [], avg_per_day: 0, avg_per_week: 0, last_30d_count: 0 });
  const [logQty, setLogQty] = useState("");
  const [logNote, setLogNote] = useState("");
  const [logging, setLogging] = useState(false);

  async function loadUsage() {
    if (!row?.id) return;
    try {
      const { data } = await api.get(`/admin/ledger/${row.id}/usage`);
      setUsage(data);
    } catch { /* silent */ }
  }

  useEffect(() => { if (open && row?.id) loadUsage(); }, [open, row?.id]);  // eslint-disable-line

  // Reset whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setPackSize(row.pack_size ?? 1);
    setUnitLabel(row.unit_label ?? "L");
    setPeriod(row.consumption_period ?? "day");
    setLight(row.consumption_light ?? 0.5);
    setMedium(row.consumption_medium ?? 1);
    setHigh(row.consumption_high ?? 2);
  }, [row?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const totalStock = useMemo(
    () => (Number(row?.quantity) || 0) * (Number(packSize) || 0),
    [row?.quantity, packSize],
  );

  function durationFor(rate) {
    const r = Number(rate);
    if (!r || !totalStock) return null;
    return totalStock / r;
  }

  /** Pretty-print a duration in the chosen period:
   *  > 30 days → "X meses"; ≥ 7 days → "X semanas"; else → "X dias". */
  function fmtDuration(value) {
    if (value === null || !Number.isFinite(value)) return "—";
    const n = Math.round(value * 10) / 10;
    if (period === "day") {
      if (n >= 30) return `${(n / 30).toFixed(1)} meses · ~${n.toFixed(0)} dias`;
      if (n >= 7) return `${(n / 7).toFixed(1)} semanas · ~${n.toFixed(0)} dias`;
      return `${n.toFixed(1)} dias`;
    }
    if (period === "week") {
      if (n >= 4) return `${(n / 4).toFixed(1)} meses · ~${n.toFixed(0)} semanas`;
      return `${n.toFixed(1)} semanas`;
    }
    return `${n.toFixed(0)} usos`;
  }

  const dLight = durationFor(light);
  const dMedium = durationFor(medium);
  const dHigh = durationFor(high);

  if (!open || !row) return null;

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/admin/ledger/${row.id}`, {
        pack_size: Number(packSize) || 0,
        unit_label: unitLabel,
        consumption_period: period,
        consumption_light: Number(light) || 0,
        consumption_medium: Number(medium) || 0,
        consumption_high: Number(high) || 0,
      });
      toast.success("Estimativa guardada.");
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="duration-modal">
      <div className="lux-card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-5 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Ruler size={12} /> Estimativa de duração
            </div>
            <div className="font-display text-xl truncate max-w-md">{row.name}</div>
          </div>
          <button onClick={onClose}
                  className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="duration-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-6">
          {/* Stock breakdown */}
          <section>
            <div className="eyebrow mb-3">Stock total deste mês</div>
            <div className="lux-card p-4 bg-[#062c27]/40">
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Embalagens</div>
                  <div className="font-display text-2xl text-[#fdfbf7]">{Number(row.quantity) || 0}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Conteúdo / embalagem</div>
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.01" min="0" value={packSize}
                           onChange={e => setPackSize(e.target.value)}
                           className="w-20 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-sm outline-none"
                           data-testid="duration-pack-size" />
                    <select value={unitLabel}
                            onChange={e => setUnitLabel(e.target.value)}
                            className="bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-1.5 py-1 text-sm outline-none"
                            data-testid="duration-unit-label">
                      {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-1 text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Stock total</div>
                  <div className="font-display text-2xl text-[#d4af37]"
                       data-testid="duration-total-stock">
                    {totalStock.toFixed(2)} {unitLabel}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Period picker */}
          <section>
            <div className="eyebrow mb-3">Período de consumo</div>
            <div className="flex gap-2 flex-wrap" data-testid="duration-period-picker">
              {[
                { k: "day", label: "Por dia" },
                { k: "week", label: "Por semana" },
                { k: "use", label: "Por uso" },
              ].map(o => (
                <button key={o.k}
                        type="button"
                        onClick={() => setPeriod(o.k)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                          period === o.k
                            ? "bg-[#d4af37] text-[#031c18] border-[#d4af37]"
                            : "border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37]"
                        }`}
                        data-testid={`duration-period-${o.k}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </section>

          {/* Three levels */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <LevelCard label="Leve" color="#10b981" icon={<TrendingDown size={14} />}
                       value={light} onChange={setLight}
                       unitLabel={unitLabel} period={period}
                       duration={fmtDuration(dLight)}
                       testKey="light" />
            <LevelCard label="Médio" color="#f59e0b" icon={<Activity size={14} />}
                       value={medium} onChange={setMedium}
                       unitLabel={unitLabel} period={period}
                       duration={fmtDuration(dMedium)}
                       testKey="medium" />
            <LevelCard label="Alto" color="#ef4444" icon={<TrendingUp size={14} />}
                       value={high} onChange={setHigh}
                       unitLabel={unitLabel} period={period}
                       duration={fmtDuration(dHigh)}
                       testKey="high" />
          </section>

          <p className="text-[11px] text-[#a0b2af] leading-relaxed">
            A duração é estimada com base no <strong className="text-[#fdfbf7]">stock total</strong>
            ({totalStock.toFixed(2)} {unitLabel}) divido pelo consumo escolhido por {PERIOD_LABEL[period]}.
            Para alterar o número de embalagens, edite a coluna <em>Qtd</em> da tabela.
          </p>

          {/* Histórico de consumo real + ajuste automático */}
          <section className="space-y-3 pt-3 border-t border-[#1a4640]" data-testid="duration-history">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="eyebrow flex items-center gap-2">
                <History size={12} className="text-[#d4af37]" /> Histórico real (últimos 30 dias)
              </div>
              {usage.last_30d_count > 0 && (
                <div className="text-xs text-[#a0b2af]">
                  Média:&nbsp;
                  <span className="text-[#d4af37] font-display">{usage.avg_per_day} {unitLabel}/dia</span>
                  &nbsp;·&nbsp;
                  <span className="text-[#d4af37] font-display">{usage.avg_per_week} {unitLabel}/semana</span>
                </div>
              )}
            </div>

            {/* Quick-log row */}
            <div className="flex items-center gap-2 flex-wrap">
              <input type="number" step="0.01" min="0" value={logQty}
                     onChange={e => setLogQty(e.target.value)}
                     placeholder={`Qtd gasta (${unitLabel})`}
                     className="w-32 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                     data-testid="duration-log-qty" />
              <input value={logNote}
                     onChange={e => setLogNote(e.target.value)}
                     placeholder="Nota (opcional)"
                     maxLength={200}
                     className="flex-1 min-w-[200px] bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
                     data-testid="duration-log-note" />
              <button onClick={async () => {
                        const q = parseFloat(logQty);
                        if (!Number.isFinite(q) || q <= 0) { toast.error("Quantidade inválida."); return; }
                        setLogging(true);
                        try {
                          await api.post(`/admin/ledger/${row.id}/log-usage`, { quantity: q, note: logNote || null });
                          setLogQty(""); setLogNote("");
                          await loadUsage();
                          toast.success("Consumo registado.");
                        } catch (err) { toast.error(formatApiError(err)); }
                        finally { setLogging(false); }
                      }}
                      disabled={logging}
                      className="btn-ghost-gold px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-50"
                      data-testid="duration-log-submit">
                <Plus size={12} /> Registar
              </button>
              {usage.avg_per_day > 0 && (
                <button onClick={() => {
                          // Auto-adjust medium to the historical daily average,
                          // converted to the chosen period.
                          let target = usage.avg_per_day;
                          if (period === "week") target = usage.avg_per_week;
                          setMedium(Math.round(target * 1000) / 1000);
                          toast.info(`Médio ajustado para ${target} ${unitLabel}/${PERIOD_LABEL[period]}.`);
                        }}
                        className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#031c18] rounded-sm transition-colors flex items-center gap-1"
                        title="Substituir o nível Médio pela média real dos últimos 30 dias"
                        data-testid="duration-auto-adjust">
                  <Wand2 size={12} /> Ajustar médio
                </button>
              )}
            </div>

            {usage.items.length > 0 ? (
              <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="duration-history-list">
                {usage.items.map((u, i) => (
                  <div key={u.id} className="flex items-center gap-2 text-xs px-2 py-1 hover:bg-[#062c27]/50 rounded-sm group">
                    <span className="text-[#a0b2af] tabular-nums w-7">#{i + 1}</span>
                    <span className="font-display text-[#d4af37] tabular-nums w-20">
                      {u.quantity} {u.unit_label}
                    </span>
                    <span className="text-[#a0b2af] flex-1 truncate">
                      {u.note || (u.logged_at || "").slice(0, 16).replace("T", " ")}
                    </span>
                    <button onClick={async () => {
                              try {
                                await api.delete(`/admin/ledger/usage/${u.id}`);
                                await loadUsage();
                              } catch (err) { toast.error(formatApiError(err)); }
                            }}
                            className="opacity-0 group-hover:opacity-100 text-[#a0b2af] hover:text-[#ef4444] transition-opacity">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-[#a0b2af] italic" data-testid="duration-history-empty">
                Sem registos. Adicione o consumo real diário para o sistema sugerir uma média mais precisa.
              </div>
            )}
          </section>

          <footer className="flex justify-end gap-3 pt-3 border-t border-[#1a4640]">
            <button onClick={onClose}
                    className="px-4 py-2.5 border border-[#1a4640] rounded-sm text-xs uppercase tracking-widest text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">
              Cancelar
            </button>
            <button onClick={save} disabled={saving}
                    className="btn-gold px-6 py-2.5 text-xs flex items-center gap-2 disabled:opacity-50"
                    data-testid="duration-save">
              {saving ? <><Loader2 size={12} className="animate-spin" /> A gravar…</> : "Guardar"}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
}

function LevelCard({ label, color, icon, value, onChange, unitLabel, period, duration, testKey }) {
  return (
    <div className="lux-card p-4 space-y-3"
         style={{ borderColor: `${color}66` }}
         data-testid={`duration-level-${testKey}`}>
      <div className="flex items-center justify-between">
        <div className="eyebrow flex items-center gap-2" style={{ color }}>
          {icon} {label}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <input type="number" step="0.01" min="0" value={value}
               onChange={e => onChange(e.target.value)}
               className="flex-1 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1.5 text-sm outline-none"
               data-testid={`duration-input-${testKey}`} />
        <span className="text-[10px] text-[#a0b2af]">{unitLabel} / {PERIOD_LABEL[period]}</span>
      </div>
      <div className="space-y-1 pt-2 border-t border-[#1a4640]">
        <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Dura</div>
        <div className="font-display text-lg" style={{ color }}
             data-testid={`duration-result-${testKey}`}>
          {duration}
        </div>
      </div>
    </div>
  );
}
